import { useState, useEffect, useRef } from 'react';
import { Upload, Loader2, AlertCircle, Check, Plus, RefreshCw, Key } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { parseEnvContent } from '../../hooks/useSecrets';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { InlineCode } from '@/components/ui/code-block';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (secrets: Array<{ key: string; value: string }>, overwrite: boolean) => Promise<{ created: number; updated: number; skipped: number }>;
  existingKeys: string[];
}

interface ParsedSecret {
  key: string;
  value: string;
  exists: boolean;
}

/**
 * Modal for bulk importing secrets from .env content
 */
export function BulkImportModal({
  isOpen,
  onClose,
  onImport,
  existingKeys,
}: BulkImportModalProps) {
  const [content, setContent] = useState('');
  const [parsedSecrets, setParsedSecrets] = useState<ParsedSecret[]>([]);
  const [overwrite, setOverwrite] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; updated: number; skipped: number; error?: string } | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setContent('');
      setParsedSecrets([]);
      setOverwrite(false);
      setResult(null);
      setParseError(null);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Parse content when it changes
  useEffect(() => {
    if (!content.trim()) {
      setParsedSecrets([]);
      setParseError(null);
      return;
    }

    try {
      const parsed = parseEnvContent(content);
      const withExists = parsed.map((s) => ({
        ...s,
        exists: existingKeys.includes(s.key),
      }));
      setParsedSecrets(withExists);
      setParseError(null);
    } catch {
      setParseError('Failed to parse content. Please check the format.');
      setParsedSecrets([]);
    }
  }, [content, existingKeys]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === 'string') {
        setContent(text);
      }
    };
    reader.onerror = () => {
      setParseError('Failed to read file');
    };
    reader.readAsText(file);

    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleImport = async () => {
    if (parsedSecrets.length === 0) return;

    setIsImporting(true);
    setResult(null);

    try {
      const secrets = parsedSecrets.map(({ key, value }) => ({ key, value }));
      const importResult = await onImport(secrets, overwrite);
      setResult(importResult);

      // Close after a short delay if successful
      if (importResult.created > 0 || importResult.updated > 0) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      setResult({
        created: 0,
        updated: 0,
        skipped: parsedSecrets.length,
        error: err instanceof Error ? err.message : 'Import failed',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const newCount = parsedSecrets.filter((s) => !s.exists).length;
  const existingCount = parsedSecrets.filter((s) => s.exists).length;

  const handleOpenChange = (open: boolean) => {
    if (!open && !isImporting) {
      onClose();
    }
  };

  const maskValue = (value: string) => {
    if (value.length <= 4) return '••••';
    return value.slice(0, 2) + '••••' + value.slice(-2);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Import Secrets
          </DialogTitle>
          <DialogDescription>
            Paste your .env file content or upload a file. Each line should be in{' '}
            <InlineCode>KEY=value</InlineCode> format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 py-4">
          {/* File upload button */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".env,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              Upload .env file
            </Button>
          </div>

          {/* Terminal-style textarea */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Or paste content directly
            </Label>
            <div className="relative rounded-lg overflow-hidden border border-border/60 bg-[hsl(225_20%_4%)] shadow-lg">
              {/* Terminal header */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[hsl(225_15%_8%)] border-b border-border/40">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                </div>
                <span className="text-xs text-muted-foreground ml-2">.env</span>
              </div>
              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`# Paste your .env content here
DATABASE_URL=postgres://user:pass@localhost:5432/db
API_KEY=sk_live_xxxxxxxxxxxxx
DEBUG=false
SECRET_TOKEN=your-secret-token`}
                className="w-full h-48 px-4 py-3 bg-transparent text-foreground placeholder:text-muted-foreground/40 font-mono text-sm resize-none focus:outline-none"
                disabled={isImporting}
                spellCheck={false}
              />
            </div>
          </div>

          {/* Parse error */}
          <AnimatePresence>
            {parseError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
              >
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{parseError}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Preview list */}
          <AnimatePresence>
            {parsedSecrets.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-3"
              >
                {/* Header with counts */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-foreground">
                      Preview
                    </h3>
                    <Badge variant="secondary" className="text-xs">
                      {parsedSecrets.length} secret{parsedSecrets.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {newCount > 0 && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1">
                        <Plus className="h-3 w-3" />
                        {newCount} new
                      </Badge>
                    )}
                    {existingCount > 0 && (
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 gap-1">
                        <RefreshCw className="h-3 w-3" />
                        {existingCount} existing
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Secrets list */}
                <div className="rounded-lg border border-border/50 bg-card/30 overflow-hidden">
                  <div className="max-h-52 overflow-y-auto">
                    {parsedSecrets.map((secret, index) => (
                      <motion.div
                        key={secret.key}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className={`flex items-center justify-between px-3 py-2.5 border-b border-border/30 last:border-b-0 ${
                          secret.exists ? 'bg-amber-500/5' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`p-1.5 rounded-md ${secret.exists ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
                            <Key className={`h-3.5 w-3.5 ${secret.exists ? 'text-amber-500' : 'text-emerald-500'}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <code className="font-mono text-sm font-medium text-foreground truncate">
                                {secret.key}
                              </code>
                            </div>
                            <div className="font-mono text-xs text-muted-foreground truncate mt-0.5">
                              {maskValue(secret.value)}
                            </div>
                          </div>
                        </div>
                        {secret.exists ? (
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${
                            overwrite
                              ? "text-amber-500 border-amber-500/30 bg-amber-500/10"
                              : "text-muted-foreground border-border bg-muted/50"
                          }`}>
                            {overwrite ? "Will update" : "Will skip"}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-500 border-emerald-500/30 bg-emerald-500/10 text-[10px] shrink-0">
                            New
                          </Badge>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Overwrite option */}
                {existingCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`flex items-center space-x-3 p-3 rounded-lg ${
                      overwrite
                        ? "bg-amber-500/5 border border-amber-500/20"
                        : "bg-muted/30 border border-border"
                    }`}
                  >
                    <Checkbox
                      id="overwrite"
                      checked={overwrite}
                      onCheckedChange={(checked) => setOverwrite(checked === true)}
                      disabled={isImporting}
                    />
                    <Label htmlFor="overwrite" className="text-sm font-normal cursor-pointer flex-1">
                      <span className={overwrite ? "font-medium text-amber-500" : "font-medium text-muted-foreground"}>
                        {existingCount}
                      </span>{' '}
                      <span className="text-muted-foreground">
                        existing secret{existingCount !== 1 ? 's' : ''} {overwrite ? 'will be updated' : 'will be skipped (check to update)'}
                      </span>
                    </Label>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Result message */}
          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`flex items-start gap-3 p-4 rounded-lg ${
                  result.error
                    ? 'bg-destructive/10 border border-destructive/20'
                    : 'bg-emerald-500/10 border border-emerald-500/20'
                }`}
              >
                {result.error ? (
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
                ) : (
                  <Check className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    result.error ? 'text-destructive' : 'text-emerald-500'
                  }`}>
                    {result.error ? (
                      'Import failed'
                    ) : (
                      <>
                        Successfully imported {result.created + result.updated} secret{result.created + result.updated !== 1 ? 's' : ''}
                        {result.created > 0 && result.updated > 0 && (
                          <span className="text-muted-foreground font-normal"> ({result.created} new, {result.updated} updated)</span>
                        )}
                      </>
                    )}
                  </p>
                  {result.skipped > 0 && !result.error && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Skipped {result.skipped} existing secret{result.skipped !== 1 ? 's' : ''}
                    </p>
                  )}
                  {result.error && (
                    <p className="text-sm text-destructive/80 mt-1">
                      {result.error}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={isImporting || parsedSecrets.length === 0}
            className="gap-2"
          >
            {isImporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Import{parsedSecrets.length > 0 ? ` ${parsedSecrets.length} Secret${parsedSecrets.length !== 1 ? 's' : ''}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
